if (!customElements.get('regloss-kit-picker-v1')) {
  customElements.define(
    'regloss-kit-picker-v1',
    class ReglossKitPickerV1 extends HTMLElement {
      constructor() {
        super();
        this.config = null;
        this.selectedTier = 'core';
        this.resolverChecked = false;
        this.isSubmitting = false;
      }

      connectedCallback() {
        const dataEl = this.querySelector('[data-regloss-kit-picker-config]');
        if (!dataEl) return;

        try {
          this.config = JSON.parse(dataEl.textContent);
        } catch (error) {
          console.error('Regloss kit picker: invalid config', error);
          return;
        }

        this.selectedTier = this.config.selectedTier || 'core';
        this.sectionId = this.config.sectionId;
        this.productInfo = document.querySelector('product-info');

        this.bindCards();
        this.bindResolver();
        this.bindForm();
        this.bindSticky();
        this.applyTier(this.selectedTier, { skipFetch: true, track: false });
      }

      bindCards() {
        this.querySelectorAll('[data-regloss-kit-tier]').forEach((card) => {
          card.addEventListener('click', () => {
            const tier = card.dataset.reglossKitTier;
            if (!tier || tier === this.selectedTier) return;
            this.applyTier(tier, { track: true });
          });
        });
      }

      bindResolver() {
        const input = this.querySelector('[data-regloss-kit-resolver]');
        if (!input) return;
        input.addEventListener('change', () => {
          this.resolverChecked = input.checked;
          this.track('fuel_01_resolver_toggle', {
            tier: this.selectedTier,
            checked: this.resolverChecked,
          });
        });
      }

      bindForm() {
        const form = this.querySelector('form[data-regloss-kit-form]');
        if (!form) return;
        form.addEventListener('submit', (event) => {
          event.preventDefault();
          const activator =
            event.submitter ||
            this.querySelector('.regloss-kit-picker-v1__submit') ||
            this.querySelector('[data-regloss-kit-sticky-submit]');
          this.submitCart(activator);
        });
      }

      applyTier(tier, options = {}) {
        const tierData = this.config.tiers?.[tier];
        if (!tierData) return;

        this.selectedTier = tier;
        this.updateCards();
        this.updateCopy(tierData);
        this.updateIncluded(tier);
        this.updateResolverVisibility();
        this.updateSubmitLabel(tierData);
        this.updateSticky(tierData);

        if (!options.skipFetch) {
          this.swapProductSection(tierData);
        }

        if (options.track !== false) {
          this.track('fuel_01_kit_selected', {
            tier,
            productId: tierData.productId,
            handle: tierData.handle,
          });
        }

        document.dispatchEvent(
          new CustomEvent('regloss:kit-picker:change', {
            bubbles: true,
            detail: {
              tier,
              variantId: tierData.variantId,
              price: tierData.price,
              title: tierData.title,
              handle: tierData.handle,
            },
          })
        );
      }

      updateCards() {
        this.querySelectorAll('[data-regloss-kit-tier]').forEach((card) => {
          card.classList.toggle('is-selected', card.dataset.reglossKitTier === this.selectedTier);
          card.setAttribute('aria-pressed', card.dataset.reglossKitTier === this.selectedTier ? 'true' : 'false');
        });
      }

      updateCopy(tierData) {
        const bestFor = this.querySelector('[data-regloss-kit-best-for]');
        if (bestFor) bestFor.innerHTML = tierData.bestForHtml || '';

        const includedTitle = this.querySelector('[data-regloss-kit-included-title]');
        if (includedTitle) includedTitle.textContent = tierData.includedTitle || '';

        const titleEl = document.querySelector('.product__title h1');
        if (titleEl && tierData.title) titleEl.textContent = tierData.title;
      }

      updateIncluded(tier) {
        const list = this.querySelector('[data-regloss-kit-included-list]');
        const template = this.querySelector(`[data-regloss-kit-included-template="${tier}"]`);
        if (!list || !template) return;

        list.replaceChildren(...template.content.cloneNode(true).childNodes);
      }

      updateResolverVisibility() {
        const wrap = this.querySelector('[data-regloss-kit-resolver-wrap]');
        const input = this.querySelector('[data-regloss-kit-resolver]');
        if (!wrap) return;

        const show = this.selectedTier === 'core' && this.config.resolver?.variantId;
        wrap.hidden = !show;
        if (!show && input) {
          input.checked = false;
          this.resolverChecked = false;
        }
      }

      updateSubmitLabel(tierData) {
        const label = this.querySelector('[data-regloss-kit-submit-label]');
        if (label && tierData.priceFormatted) {
          label.textContent = `Add to cart - ${tierData.priceFormatted}`;
        }

        const variantInput = this.querySelector('[data-regloss-kit-variant-id]');
        if (variantInput) variantInput.value = tierData.variantId;

        const submitButton = this.querySelector('.regloss-kit-picker-v1__submit');
        if (submitButton) submitButton.disabled = !tierData.available;
        if (variantInput) variantInput.disabled = !tierData.available;
      }

      bindSticky() {
        const stickyBar = this.querySelector('[data-regloss-kit-sticky]');
        const stickySubmit = this.querySelector('[data-regloss-kit-sticky-submit]');
        if (!stickyBar) return;

        /* Escape product column — ancestor transform/filter breaks position:fixed */
        if (stickyBar.parentElement !== document.body) {
          document.body.appendChild(stickyBar);
          stickyBar.classList.add('regloss-kit-picker-v1__sticky--body');
        }
        this.stickyBar = stickyBar;

        this.syncStickyScroll = () => {
          const show = window.scrollY > 0;
          stickyBar.classList.toggle('is-visible', show);
          stickyBar.setAttribute('aria-hidden', show ? 'false' : 'true');
          document.documentElement.classList.toggle('regloss-kit-picker-sticky-active', show);
        };
        window.addEventListener('scroll', this.syncStickyScroll, { passive: true });
        this.syncStickyScroll();

        stickySubmit?.addEventListener('click', () => {
          if (this.isSubmitting) return;
          this.submitCart(stickySubmit);
        });
      }

      disconnectedCallback() {
        if (this.syncStickyScroll) {
          window.removeEventListener('scroll', this.syncStickyScroll);
        }
        document.documentElement.classList.remove('regloss-kit-picker-sticky-active');
      }

      updateSticky(tierData) {
        const stickyRoot = this.stickyBar || this;
        const title = stickyRoot.querySelector('[data-regloss-kit-sticky-title]');
        const price = stickyRoot.querySelector('[data-regloss-kit-sticky-price]');
        const label = stickyRoot.querySelector('[data-regloss-kit-sticky-submit-label]');
        const image = stickyRoot.querySelector('[data-regloss-kit-sticky-image]');
        const stickySubmit = stickyRoot.querySelector('[data-regloss-kit-sticky-submit]');

        if (title && tierData.title) title.textContent = tierData.title;
        if (price && tierData.priceFormatted) price.textContent = tierData.priceFormatted;
        if (label) label.textContent = 'Add to cart';
        if (image && tierData.featuredImage) {
          image.src = tierData.featuredImage;
          image.alt = tierData.title || '';
        }
        if (stickySubmit) stickySubmit.disabled = !tierData.available;
      }

      async swapProductSection(tierData) {
        if (!this.sectionId || !tierData.handle) return;

        const url = `${window.shopUrl}/products/${tierData.handle}?sections=${this.sectionId}`;

        try {
          const response = await fetch(url);
          const data = await response.json();
          const html = data[this.sectionId];
          if (!html) return;

          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newMedia = doc.querySelector('.product__media-wrapper');
          const currentMedia = document.querySelector('.product__media-wrapper');

          if (newMedia && currentMedia) {
            currentMedia.innerHTML = newMedia.innerHTML;
          }

          const newPayment = doc.querySelector('.regloss-kit-picker-v1__payment-slot');
          const paymentSlot = this.querySelector('.regloss-kit-picker-v1__payment-slot');
          if (newPayment && paymentSlot) {
            paymentSlot.innerHTML = newPayment.innerHTML;
            window?.Shopify?.PaymentButton?.init();
          }

          if (this.productInfo) {
            this.productInfo.dataset.productId = tierData.productId;
          }
        } catch (error) {
          console.error('Regloss kit picker: section swap failed', error);
        }
      }

      async submitCart(activator) {
        if (this.isSubmitting) return;

        const tierData = this.config.tiers[this.selectedTier];
        if (!tierData?.variantId) return;

        const submitButton = activator || this.querySelector('.regloss-kit-picker-v1__submit');
        const spinner = submitButton?.querySelector('.loading__spinner');

        this.isSubmitting = true;
        submitButton?.setAttribute('aria-disabled', 'true');
        submitButton?.classList.add('loading');
        spinner?.classList.remove('hidden');

        const items = [{ id: tierData.variantId, quantity: 1 }];
        if (this.resolverChecked && this.config.resolver?.variantId) {
          items.push({ id: this.config.resolver.variantId, quantity: 1 });
        }

        this.track('fuel_01_add_to_cart', {
          tier: this.selectedTier,
          variantId: tierData.variantId,
          resolver: this.resolverChecked,
        });

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        try {
          let result = null;

          for (let index = 0; index < items.length; index += 1) {
            const formData = new FormData();
            formData.append('id', items[index].id);
            formData.append('quantity', items[index].quantity);

            if (index === items.length - 1 && cart?.getSectionsToRender) {
              formData.append(
                'sections',
                cart.getSectionsToRender().map((section) => section.id)
              );
              formData.append('sections_url', window.location.pathname);
              if (cart.setActiveElement) cart.setActiveElement(document.activeElement);
            }

            config.body = formData;
            const response = await fetch(`${routes.cart_add_url}`, config);
            result = await response.json();
            if (result.status) break;
          }

          if (result.status) {
            this.showError(result.description || result.message);
            return;
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'regloss-kit-picker-v1',
            productVariantId: tierData.variantId,
            cartData: result,
          });

          if (cart?.renderContents) {
            cart.renderContents(result);
          } else {
            window.location = window.routes.cart_url;
          }
        } catch (error) {
          console.error(error);
          this.showError(window.cartStrings?.error);
        } finally {
          this.isSubmitting = false;
          submitButton?.classList.remove('loading');
          submitButton?.removeAttribute('aria-disabled');
          spinner?.classList.add('hidden');
        }
      }

      showError(message) {
        const wrapper = this.querySelector('.product-form__error-message-wrapper');
        const messageEl = this.querySelector('.product-form__error-message');
        if (!wrapper || !messageEl || !message) return;
        wrapper.hidden = false;
        messageEl.textContent = message;
      }

      track(eventName, payload) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: eventName, experiment: 'ab-kit-picker', ...payload });

        document.documentElement.dispatchEvent(
          new CustomEvent('intelligems:track', {
            detail: { event: eventName, ...payload },
          })
        );
      }
    }
  );
}
